import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Frequency, RRule, rrulestr, Weekday } from 'rrule';
import { RecurrenceOptions } from 'src/graphql/entities/signal-oncall-schedule/signal-oncall-schedule.types';
import { CalendarEventObject, CalendarView } from 'src/pages/Settings/signal-oncall-schedule/types';
import { WEEKLY_RECURRENCE_OPTIONS } from './const';
dayjs.extend(utc);
dayjs.extend(timezone);

const AGENDA_NAVIGATION_DAYS_COUNT = 30;

// Beware that this function will return a value converted to local timezone.
export function mergeDateAndTime(date: Date | string, time: string) {
  const [hours, minutes, seconds] = time.split(':');
  const newDate = new Date(date);
  newDate.setHours(Number(hours));
  newDate.setMinutes(Number(minutes));
  newDate.setSeconds(Number(seconds));
  return newDate;
}

export function getWeekBoundariesInUTC(date: Date) {
  const weekStart = dayjs(date).day(0).startOf('day').tz('UTC', true).toDate();
  // This is an improvised workaround to fix time shifting during DST period.
  // As it turned out, the created date object may have hours set to 1 instead of 0 (for week start),
  // or 22 instead of 23 (for week end).
  // So to ensure correct week boundaries, the hours are reset manually.

  // TODO: Investigate if there's a cleaner way to achieve this.
  weekStart.setUTCHours(0);

  // Subtracting 1 ms to stay within week limits (otherwise weekEnd would be next week start).
  const weekEnd = dayjs(weekStart).add(1, 'week').subtract(1, 'millisecond').toDate();

  weekEnd.setUTCHours(23);
  return [weekStart, weekEnd];
}

export function getWeekBoundariesInSpecifiedTimezone(date: Date, timezone: string) {
  const weekStart = dayjs(date).day(0).startOf('day').tz(timezone, true).toDate();
  // Subtracting 1 ms to stay within week limits (otherwise weekEnd would be next week start).
  const weekEnd = dayjs(weekStart).add(1, 'week').subtract(1, 'millisecond').toDate();
  return [weekStart, weekEnd];
}

export function getDayBoundariesInSpecifiedTimezone(date: Date, timezone: string) {
  const dayStart = dayjs(date).tz(timezone, true).startOf('day').toDate();
  const dayEnd = dayjs(dayStart).add(1, 'day').subtract(1, 'millisecond').toDate();
  return [dayStart, dayEnd];
}

export function getAgendaBoundariesInSpecifiedTimezone(
  date: Date,
  timezone: string,
  daysCount = 30,
) {
  const agendaStart = dayjs(date).tz(timezone, true).startOf('day').toDate();
  const agendaEnd = dayjs(agendaStart).add(daysCount, 'day').subtract(1, 'millisecond').toDate();

  return [agendaStart, agendaEnd];
}

export function getToolbarStepDateByView(
  date: Date,
  view: CalendarView,
  direction: 'previous' | 'next',
): Date {
  const offset = direction === 'next' ? 1 : -1;

  if (view === 'day') {
    return dayjs(date).add(offset, 'day').startOf('day').toDate();
  }

  if (view === 'agenda') {
    return dayjs(date)
      .add(offset * AGENDA_NAVIGATION_DAYS_COUNT, 'day')
      .startOf('day')
      .toDate();
  }

  return dayjs(date).add(offset, 'week').day(0).startOf('day').toDate();
}

export function isAllDaySlotSelected(startTime: Date, endTime: Date) {
  /**
   *  If start and end time are both 00:00 and the dates are different,
   *  it means that a full-day event slot was selected on the calendar
   *  (library's behavior).
   *
   *  This callback is invoked upon user interaction with the calendar.
   */
  const isAtLeastDayLong =
    startTime.getHours() === 0 &&
    endTime.getHours() === 0 &&
    !dayjs(startTime).isSame(endTime, 'day');

  return isAtLeastDayLong;
}

// Day duration from 00:00:00 till 23:59:59.999 (to keep the same date).
const ALL_DAY_MS = 86400000 - 1;
export function isAllDaySchedule(schedule: { end: Date; start: Date }) {
  /**
   * Schedules that have been created as all-day events have ALL_DAY_MS
   * duration to fit within a single day.
   */

  const start = dayjs(convertToUTCDate(schedule.start));
  const end = dayjs(convertToUTCDate(schedule.end));

  return end.diff(start, 'ms') === ALL_DAY_MS;
}

export function getRecurrenceInterval(recurrence = '') {
  try {
    return rrulestr(recurrence).options.interval;
  } catch {
    return undefined;
  }
}

export function getRecurrencePeriod(recurrence = ''): Frequency | undefined {
  try {
    return rrulestr(recurrence).options.freq;
  } catch {
    return undefined;
  }
}

export function getRecurrenceDays(recurrence = ''): Weekday[] {
  try {
    return (rrulestr(recurrence).origOptions.byweekday || []) as Weekday[];
  } catch {
    return [];
  }
}

/*
  The functions below although look similar, have different purposes.
  It is possible that there's an overlap and something could be refactored
  for better simplicity, but nothing obvious comes to mind, and it's not
  a priority at the moment.
*/

export function convertToUTCDate(date: Date) {
  return buildUTCDate({
    y: date.getFullYear(),
    m: date.getMonth(),
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    ms: date.getMilliseconds(),
  });
}

function buildUTCDate({
  y,
  m,
  d,
  h = 0,
  i = 0,
  s = 0,
  ms = 0,
}: {
  y: number;
  m: number;
  d: number;
  h?: number;
  i?: number;
  s?: number;
  ms?: number;
}) {
  return new Date(Date.UTC(y, m, d, h, i, s, ms));
}

export function convertDateTimeToDate(datetime: Date, timeZoneName: string) {
  const m = dayjs.tz(datetime, timeZoneName);
  return new Date(
    m.year(),
    m.month(),
    m.date(),
    m.hour(),
    m.minute(),
    m.second(),
    m.millisecond(),
  );
}

export function treatUTCDateAsLocal(date: Date) {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  );
}

export const TIME_FORMAT = 'HH:mm:ss';
export function isWeeklyPeriodSelected({
  recurrence,
  recurrencePeriod,
}: {
  recurrence: string;
  recurrencePeriod?: Frequency;
}): boolean {
  return (
    WEEKLY_RECURRENCE_OPTIONS.includes(recurrence as RecurrenceOptions) ||
    recurrencePeriod === RRule.WEEKLY
  );
}
export function canEditCurrentEvent(data?: CalendarEventObject | null) {
  return !!data && !!data.id && data.recurrence !== RecurrenceOptions.Once;
}
